// src/services/data/gatheringService.ts
import { Gathering, GatheringStatus } from '../../types';
import {
  fetchGatheringsByStatus,
  fetchGatheringById,
  fetchGatheringsForMonth,
  fetchGatheringsForWeek,
  fetchGatheringsByDateRange,
  updateGatheringStatus,
  updateGatheringDetails,
  updateGatheringStatusAsAdmin
} from '../api/endpoints/gatherings';
import { invalidateCache } from '../api/cache';
import { formatDateForApi } from '../api/utils';
import {
  store,
  addGathering,
  updateGathering,
  clearStore
} from './store';
import { refreshAuthToken } from '../firebase';
import { isAbortError } from '../api/utils/concurrencyLimit';

// Type for filter options
export interface GatheringFilterOptions {
  fromDate: string | null;
  toDate: string | null;
  spaceId: string | null;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

// Helper to get default fromDate (30 days ago)
const getDefaultFromDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return formatDateForApi(date);
};

// Default filter options - 30 days ago to limit initial data load
const defaultFilters: GatheringFilterOptions = {
  fromDate: getDefaultFromDate(),
  toDate: null,
  spaceId: null,
  orderBy: 'date',
  orderDirection: 'asc'
};

class GatheringDataService {
  private token: string | null = null;
  private currentFilters: GatheringFilterOptions = { ...defaultFilters };

  // AbortController for the current fanout "scope". Background page-loaders
  // and calendar fan-outs can pile up many in-flight requests; when the
  // user switches filters or fires a search those previously-queued fetches
  // are now stale and should be cancelled (frees up limiter slots, saves
  // backend pool, prevents stale data from clobbering fresh state).
  //
  // The controller is created lazily on first `_getSignal()` and cleared
  // by `cancelInFlightFetches()`. Each long-running operation captures the
  // signal at call time, so cancellation propagates to everything that was
  // dispatched under the old scope.
  private _activeFetchController: AbortController | null = null;

  private _getSignal(): AbortSignal {
    if (!this._activeFetchController) {
      this._activeFetchController = new AbortController();
    }
    return this._activeFetchController.signal;
  }

  /**
   * Aborts the previous fanout scope and starts a fresh one. Called
   * automatically by setFilters/resetFilters. Safe to call externally
   * before a search/filter UI change to drop stale queued fetches.
   */
  cancelInFlightFetches(): void {
    if (this._activeFetchController) {
      this._activeFetchController.abort();
      this._activeFetchController = null;
    }
  }

  // Set the authentication token
  setToken(token: string | null): void {
    this.token = token;
  }

  // Get the current token, refreshing it if needed
  async getToken(): Promise<string | null> {
    // If no token is set, try to refresh it
    if (!this.token) {
      try {
        const newToken = await refreshAuthToken();
        if (newToken) {
          this.setToken(newToken);
          return newToken;
        }
      } catch (error) {
        // Silent failure - no need to log errors during token refresh
      }
    }

    return this.token;
  }

  // Set filter options
  setFilters(filters: Partial<GatheringFilterOptions>): void {
    this.currentFilters = { ...this.currentFilters, ...filters };

    // Drop in-flight requests from the previous filter scope so the
    // limiter slots free up immediately for fresh queries.
    this.cancelInFlightFetches();

    // Invalidate API cache when filters change
    invalidateCache('/gatherings');
  }

  // Reset filters to default
  resetFilters(): void {
    this.currentFilters = { ...defaultFilters };

    // Drop in-flight requests from the previous filter scope.
    this.cancelInFlightFetches();

    // Invalidate API cache
    invalidateCache('/gatherings');
  }
  
  // Get current filters
  getFilters(): GatheringFilterOptions {
    return { ...this.currentFilters };
  }
  
  /**
   * Updates the status of a gathering with optimistic UI updates
   * @param gatheringId The ID of the gathering to update
   * @param newStatus The new status to set
   * @returns Promise that resolves when the update is complete
   */
  async updateGatheringStatus(gatheringId: string, newStatus: GatheringStatus): Promise<void> {
    if (!this.token) {
      console.warn('Authentication token not set. Attempting to update gathering status without authentication.');
      return Promise.reject(new Error('Authentication token not set'));
    }
    
    // Get the current gathering from the store
    const gathering = store.entities.gatherings[gatheringId];
    if (!gathering) {
      console.error(`Gathering with ID ${gatheringId} not found in store`);
      return Promise.reject(new Error(`Gathering with ID ${gatheringId} not found in store`));
    }
    
    // Store the original status for rollback if needed
    const originalStatus = gathering.status;
    
    try {
      // Optimistic update - update the store immediately
      const updatedGathering = { ...gathering, status: newStatus };
      updateGathering(updatedGathering);
      
      // Call the API to update the status
      await updateGatheringStatus(this.token, gatheringId, newStatus);
      
      // Invalidate cache for the affected statuses
      invalidateCache(`/gatherings/search?status=${originalStatus}`);
      invalidateCache(`/gatherings/search?status=${newStatus}`);
      
      // No need to re-fetch the data - the optimistic update and store notification
      // mechanism will ensure the UI updates correctly
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating gathering status:', error);
      
      // Rollback the optimistic update on error
      if (gathering) {
        const rollbackGathering = { ...gathering, status: originalStatus };
        updateGathering(rollbackGathering);
      }
      
      return Promise.reject(error);
    }
  }
  
  /**
   * Updates a gathering with optimistic UI updates
   * @param gatheringId The ID of the gathering to update
   * @param gatheringData The data to update
   * @returns Promise that resolves with the updated gathering
   */
  async updateGatheringDetails(
    gatheringId: string, 
    gatheringData: Partial<Gathering>
  ): Promise<Gathering> {
    if (!this.token) {
      console.warn('Authentication token not set. Attempting to update gathering without authentication.');
      return Promise.reject(new Error('Authentication token not set'));
    }
    
    // Get the current gathering from the store
    const gathering = store.entities.gatherings[gatheringId];
    if (!gathering) {
      console.error(`Gathering with ID ${gatheringId} not found in store`);
      return Promise.reject(new Error(`Gathering with ID ${gatheringId} not found in store`));
    }
    
    // Store the original gathering for rollback if needed
    const originalGathering = { ...gathering };
    
    try {
      // Optimistic update - update the store immediately
      const updatedGathering = { ...gathering, ...gatheringData };
      updateGathering(updatedGathering);
      
      // Call the API to update the gathering
      const response = await updateGatheringDetails(this.token, gatheringId, gatheringData);
      
      // Update the store with the response from the API
      // This ensures we have the complete and correct data
      updateGathering(response);
      
      return response;
    } catch (error) {
      console.error('Error updating gathering:', error);
      
      // Rollback the optimistic update on error
      updateGathering(originalGathering);
      
      return Promise.reject(error);
    }
  }
  
  /**
   * Updates the status of a gathering as admin with optional notes for NEED_MORE_INFO status
   * @param gatheringId The ID of the gathering to update
   * @param newStatus The new status to set
   * @param notes Optional notes to include (only used for NEED_MORE_INFO status)
   * @returns Promise that resolves when the update is complete
   */
  async updateGatheringStatusAsAdmin(
    gatheringId: string, 
    newStatus: GatheringStatus, 
    notes?: string
  ): Promise<void> {
    if (!this.token) {
      console.warn('Authentication token not set. Attempting to update gathering status without authentication.');
      return Promise.reject(new Error('Authentication token not set'));
    }
    
    // Get the current gathering from the store
    const gathering = store.entities.gatherings[gatheringId];
    if (!gathering) {
      console.error(`Gathering with ID ${gatheringId} not found in store`);
      return Promise.reject(new Error(`Gathering with ID ${gatheringId} not found in store`));
    }
    
    // Store the original status for rollback if needed
    const originalStatus = gathering.status;
    
    try {
      // Optimistic update - update the store immediately
      const updatedGathering = { ...gathering, status: newStatus };
      updateGathering(updatedGathering);
      
      // Call the API to update the status with notes
      await updateGatheringStatusAsAdmin(this.token, gatheringId, newStatus, notes);
      
      // Invalidate cache for the affected statuses
      invalidateCache(`/gatherings/search?status=${originalStatus}`);
      invalidateCache(`/gatherings/search?status=${newStatus}`);
      
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating gathering status:', error);
      
      // Rollback the optimistic update on error
      if (gathering) {
        const rollbackGathering = { ...gathering, status: originalStatus };
        updateGathering(rollbackGathering);
      }
      
      return Promise.reject(error);
    }
  }
  
  /**
   * Loads gatherings by status with pagination
   * @param status The status to filter by
   * @param page The page number to load
   * @returns Promise containing the response with pagination metadata
   */
  async loadGatheringsByStatus(
    status: GatheringStatus,
    page: number = 1
  ): Promise<{
    gatherings: Gathering[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    hasMore: boolean;
  }> {
    // Get the current token, attempting to refresh if needed
    const token = await this.getToken();
    
    if (!token) {
      throw new Error('Authentication token not set');
    }
    
    // Update loading state
    store.metadata.isLoading.byStatus[status] = true;

    try {
      const response = await fetchGatheringsByStatus(
        token,
        status,
        page,
        this.currentFilters.orderBy || 'date',
        this.currentFilters.orderDirection || 'asc',
        this.currentFilters.fromDate,
        this.currentFilters.toDate,
        this.currentFilters.spaceId,
        this._getSignal()
      );
      
      // Update our store with normalized data
      // Only clear the status array if this is a fresh load (page 1) AND not part of a multi-page load
      // We'll track multi-page loads using a flag in the metadata
      const isMultiPageLoad = store.metadata.pagination.byStatus[status]?.isLoadingAllPages || false;
      
      if (page === 1 && !isMultiPageLoad) {
        // Clear existing gatherings for this status only if this is not part of a multi-page load
        store.views.byStatus[status] = [];
        
        // Set the flag to indicate we're in a multi-page load if there are multiple pages
        if (response.total_pages > 1) {
          if (!store.metadata.pagination.byStatus[status]) {
            store.metadata.pagination.byStatus[status] = {
              currentPage: response.current_page,
              totalPages: response.total_pages,
              hasMore: response.current_page < response.total_pages,
              isLoadingAllPages: true
            };
          } else {
            store.metadata.pagination.byStatus[status].isLoadingAllPages = true;
          }
        }
      }
      
      // Add each gathering to the store
      response.items.forEach(gathering => {
        if (page === 1 || !store.entities.gatherings[gathering.id]) {
          addGathering(gathering);
        } else {
          updateGathering(gathering);
          
          // Ensure it's in the correct status view array
          const statusArray = store.views.byStatus[gathering.status];
          if (!statusArray.includes(gathering.id)) {
            statusArray.push(gathering.id);
          }
        }
      });
      
      // Update metadata
      store.metadata.lastFetched.byStatus[status] = Date.now();
      store.metadata.isLoading.byStatus[status] = false;
      
      // Update pagination info, but preserve the isLoadingAllPages flag if it exists
      const isLoadingAllPages = store.metadata.pagination.byStatus[status]?.isLoadingAllPages || false;
      store.metadata.pagination.byStatus[status] = {
        currentPage: response.current_page,
        totalPages: response.total_pages,
        hasMore: response.current_page < response.total_pages,
        isLoadingAllPages: isLoadingAllPages
      };
      
      // If this is the last page in a multi-page load, reset the flag
      if (page === response.total_pages && isLoadingAllPages) {
        store.metadata.pagination.byStatus[status].isLoadingAllPages = false;
      }
      
      // Return gatherings for this status from the store
      const gatheringIds = store.views.byStatus[status];
      const gatherings = gatheringIds.map(id => store.entities.gatherings[id]);
      
      return {
        gatherings,
        totalCount: response.total_count,
        totalPages: response.total_pages,
        currentPage: response.current_page,
        hasMore: response.current_page < response.total_pages
      };
    } catch (error) {
      store.metadata.isLoading.byStatus[status] = false;
      // Filter changes deliberately cancel in-flight fetches; demote
      // aborts to debug so the console isn't flooded with red on every
      // search. Still rethrow so callers can short-circuit cleanup work.
      if (isAbortError(error)) {
        console.debug(`Cancelled in-flight load for ${status} gatherings`);
      } else {
        console.error(`Error loading ${status} gatherings:`, error);
      }
      throw error;
    }
  }
  
  // Get gatherings for a specific status from store (no API call)
  getGatheringsByStatus(status: GatheringStatus): Gathering[] {
    const gatheringIds = store.views.byStatus[status] || [];
    return gatheringIds.map(id => store.entities.gatherings[id]).filter(Boolean);
  }
  
  // Check if a status is currently loading
  isStatusLoading(status: GatheringStatus): boolean {
    return store.metadata.isLoading.byStatus[status];
  }
  
  // Get all gatherings across all statuses
  getAllGatherings(): Gathering[] {
    return Object.values(store.entities.gatherings);
  }
  
  // Load gatherings for calendar month view
  async loadGatheringsForMonth(year: number, month: number): Promise<Gathering[]> {
    if (!this.token) {
      throw new Error('Authentication token not set');
    }
    
    const dateKey = `${year}-${month.toString().padStart(2, '0')}`;
    store.metadata.isLoading.byDate[dateKey] = true;
    
    try {
      const gatherings = await fetchGatheringsForMonth(
        this.token,
        year,
        month,
        this.currentFilters.spaceId,
        this._getSignal()
      );

      // Update our store with normalized data
      gatherings.forEach(gathering => {
        if (!store.entities.gatherings[gathering.id]) {
          addGathering(gathering);
        } else {
          updateGathering(gathering);
        }
      });

      // Update metadata
      store.metadata.lastFetched.byDate[dateKey] = Date.now();
      store.metadata.isLoading.byDate[dateKey] = false;

      return gatherings;
    } catch (error) {
      store.metadata.isLoading.byDate[dateKey] = false;
      if (isAbortError(error)) {
        console.debug(`Cancelled in-flight month load for ${dateKey}`);
      } else {
        console.error(`Error loading gatherings for month ${dateKey}:`, error);
      }
      throw error;
    }
  }

  // Load gatherings for a custom date range
  async loadGatheringsForDateRange(startDate: Date, endDate: Date): Promise<Gathering[]> {
    if (!this.token) {
      throw new Error('Authentication token not set');
    }
    
    // Format dates for the store metadata
    const startKey = formatDateForApi(startDate);
    const endKey = formatDateForApi(endDate);
    const dateRangeKey = `${startKey}_${endKey}`;
    
    // Set loading state
    store.metadata.isLoading.byDate[dateRangeKey] = true;

    // Capture the signal once so all paginated fetches share the same
    // scope — if the user filters mid-load, every queued page fetch
    // aborts together.
    const signal = this._getSignal();

    try {
      // We'll use a direct search approach without filtering by status
      // First, get the first page to determine total pages
      let allGatherings: Gathering[] = [];
      let currentPage = 1;

      // Make the first request to get initial data and pagination info
      const firstPageResponse = await fetchGatheringsByDateRange(
        this.token,
        startKey,
        endKey,
        currentPage,
        'date',
        'asc',
        this.currentFilters.spaceId,
        signal
      );

      // Add gatherings from first page
      allGatherings = [...allGatherings, ...firstPageResponse.items];

      // Check if there are more pages
      const totalPages = firstPageResponse.total_pages;

      // If there are more pages, fetch them all in parallel. The shared
      // limiter inside fetchGatheringsByDateRange caps backend pressure
      // at 3 concurrent regardless of how many pages we fan out.
      if (totalPages > 1) {
        const additionalPagePromises = [];

        // Create promises for pages 2 to totalPages
        for (let page = 2; page <= totalPages; page++) {
          additionalPagePromises.push(
            fetchGatheringsByDateRange(
              this.token,
              startKey,
              endKey,
              page,
              'date',
              'asc',
              this.currentFilters.spaceId,
              signal
            ).catch(error => {
              if (isAbortError(error)) {
                console.debug(
                  `[gatheringService] Cancelled date-range page ${page}`
                );
              } else {
                console.error(
                  `[gatheringService] Error loading page ${page}:`,
                  error
                );
              }
              // Return empty results so other pages can continue loading.
              // For aborts the caller's outer try/catch handles overall
              // cancellation; per-page failures shouldn't poison the batch.
              return { items: [] };
            })
          );
        }

        // Wait for all additional pages to load
        const additionalResponses = await Promise.all(additionalPagePromises);

        // Add gatherings from all additional pages
        additionalResponses.forEach(response => {
          allGatherings = [...allGatherings, ...response.items];
        });
      }
      
      // Update our store with normalized data
      allGatherings.forEach(gathering => {
        if (!store.entities.gatherings[gathering.id]) {
          // New gathering - add it normally
          addGathering(gathering);
        } else {
          // Existing gathering - update it
          updateGathering(gathering);
          
          // IMPORTANT: Also ensure it's in the correct status view array
          // This fixes issues when switching between views
          const statusArray = store.views.byStatus[gathering.status];
          if (!statusArray.includes(gathering.id)) {
            statusArray.push(gathering.id);
          }
        }
      });
      
      // Update metadata
      store.metadata.lastFetched.byDate[dateRangeKey] = Date.now();
      store.metadata.isLoading.byDate[dateRangeKey] = false;
      
      return allGatherings;
    } catch (error) {
      store.metadata.isLoading.byDate[dateRangeKey] = false;
      if (isAbortError(error)) {
        console.debug(
          `[gatheringService] Cancelled date-range load ${startKey} to ${endKey}`
        );
      } else {
        console.error(
          `[gatheringService] Error loading gatherings for date range ${startKey} to ${endKey}:`,
          error
        );
      }
      throw error;
    }
  }
  
  // Get gatherings for a date range (no API call)
  getGatheringsForDateRange(startDate: Date, endDate: Date): Gathering[] {
    // Format dates to YYYY-MM-DD for comparison
    const start = formatDateForApi(startDate);
    const end = formatDateForApi(endDate);
    
    // Get all gatherings
    const allGatherings = Object.values(store.entities.gatherings);
    
    // Filter gatherings within the date range
    const filteredGatherings = allGatherings.filter(gathering => {
      const gatheringDate = gathering.date.split('T')[0]; // Extract YYYY-MM-DD part
      
      const isInRange = gatheringDate >= start && gatheringDate <= end;
      
      return isInRange;
    });
    
    return filteredGatherings;
  }
  
  // Get a single gathering by ID
  async getGatheringById(id: string): Promise<Gathering> {
    if (!this.token) {
      throw new Error('Authentication token not set');
    }
    
    // Check if we already have this gathering in the store
    const cachedGathering = store.entities.gatherings[id];
    
    // If we have it and it's fresh (fetched within the last 30 seconds), return it
    if (cachedGathering) {
      const lastFetchedStatus = store.metadata.lastFetched.byStatus[cachedGathering.status];
      if (lastFetchedStatus && Date.now() - lastFetchedStatus < 30000) {
        return cachedGathering;
      }
    }
    
    try {
      const gathering = await fetchGatheringById(this.token, id);
      
      // Update the store
      if (!store.entities.gatherings[gathering.id]) {
        addGathering(gathering);
      } else {
        updateGathering(gathering);
      }
      
      return gathering;
    } catch (error) {
      throw error;
    }
  }
  
  // Refresh all data
  async refreshAllData(): Promise<void> {
    // Invalidate the API cache
    invalidateCache();
    
    // Don't clear the store - keep existing data while fetching
    // clearStore();
    
    if (!this.token) return;
    
    // Helper: build a per-page promise that swallows-to-empty per-page
    // failures (so other pages keep loading) while demoting deliberate
    // aborts to debug-level noise.
    const loadPageOrEmpty = (status: GatheringStatus, page: number) =>
      this.loadGatheringsByStatus(status, page).catch(error => {
        if (isAbortError(error)) {
          console.debug(`Cancelled page ${page} for ${status} (refreshAllData)`);
        } else {
          console.error(`Error loading page ${page} for ${status}:`, error);
        }
        return {
          gatherings: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: page,
          hasMore: false
        };
      });

    // Load the NEW status first (most important)
    const newStatusResponse = await this.loadGatheringsByStatus(GatheringStatus.NEW, 1);

    // If there are more pages for NEW status, load them in the background
    if (newStatusResponse.totalPages > 1) {
      const additionalPagePromises = [];
      for (let page = 2; page <= newStatusResponse.totalPages; page++) {
        additionalPagePromises.push(loadPageOrEmpty(GatheringStatus.NEW, page));
      }
      await Promise.all(additionalPagePromises);
    }

    // Then load the other statuses
    for (const status of Object.values(GatheringStatus)) {
      if (status !== GatheringStatus.NEW) {
        // Load first page
        const statusResponse = await this.loadGatheringsByStatus(status, 1);

        // If there are more pages, load them in the background
        if (statusResponse.totalPages > 1) {
          const additionalPagePromises = [];
          for (let page = 2; page <= statusResponse.totalPages; page++) {
            additionalPagePromises.push(loadPageOrEmpty(status, page));
          }
          await Promise.all(additionalPagePromises);
        }
      }
    }
  }
}

// Create a singleton instance
export const gatheringDataService = new GatheringDataService();

// Export the singleton
export default gatheringDataService;